"use client";

import type { ReactNode } from "react";

import { Eyebrow, InlineEditableField, QualityDiamonds } from "@/components/ui";
import type { UploadFileKind } from "@/lib/upload/score-file";

/**
 * The glyph, the badge and the eyebrow, per family.
 *
 * A MIDI is drawn as piano-roll bands because that is what it is — a
 * performance, notes on a time grid. A MusicXML and a scanned page are both
 * drawn as staves, because both are an engraved score; only the badge tells
 * them apart.
 */
const SOURCE_VISUALS: Record<
  UploadFileKind,
  { eyebrow: string; badge: string; glyph: "roll" | "staff" }
> = {
  midi: { eyebrow: "Morceau", badge: "MIDI", glyph: "roll" },
  musicxml: { eyebrow: "Partition", badge: "MUSICXML", glyph: "staff" },
  image: { eyebrow: "Partition", badge: "PDF", glyph: "staff" },
};

/**
 * Props for {@link PieceCard}.
 */
export interface PieceCardProps {
  /** Original filename (e.g. `zombie.mid`). */
  filename: string;
  /** Pre-formatted file size (e.g. `"14 ko"`). */
  fileSize: string;
  /** Family of the imported file, driving the glyph and the eyebrow wording. */
  source: UploadFileKind;

  /** Current title value (controlled). Empty string shows placeholder. */
  title?: string;
  /** Current composer value (controlled). Empty string shows placeholder. */
  composer?: string;
  /** Title change handler. Required for editable rendering. */
  onTitleChange?: (next: string) => void;
  /** Composer change handler. Required for editable rendering. */
  onComposerChange?: (next: string) => void;
  /** Placeholder for the title field. Defaults to `"Sans titre"`. */
  titlePlaceholder?: string;
  /** Placeholder for the composer field. Defaults to `"Ajouter un compositeur"`. */
  composerPlaceholder?: string;

  /** Detected key label (e.g. `"Mi mineur"`). Falsy values hide the row. */
  detectedKey?: string;
  /** Quality score, 1..5. Falsy values render five unlit diamonds. */
  quality?: number;
  /** Free-form quality description (e.g. `"Analyse fiable"`). */
  qualityLabel?: string;

  /**
   * When true, replaces every editable / detected zone with a shimmer
   * skeleton. Used during the `analyzing` phase.
   */
  loading?: boolean;
  /** Optional content rendered below the footer grid (e.g. context hint). */
  footerExtras?: ReactNode;
}

const TITLE_PLACEHOLDER = "Sans titre";
const COMPOSER_PLACEHOLDER = "Ajouter un compositeur";

/**
 * Hero card of the upload modal that gives the user a sense of "Virtuosa
 * sees my file". Renders the filename, a stylised file glyph, the editable
 * metadata (title, composer), and the analyzer verdict (key, quality).
 *
 * Layout pixel-precise per the Wave 2D spec, all colors flow through tokens
 * (`--color-border-subtle`, `--color-glass-separator`, …). Stateless: the
 * parent owns title/composer state (anti-stomp contract).
 */
export function PieceCard({
  filename,
  fileSize,
  source,
  title = "",
  composer = "",
  onTitleChange,
  onComposerChange,
  titlePlaceholder = TITLE_PLACEHOLDER,
  composerPlaceholder = COMPOSER_PLACEHOLDER,
  detectedKey,
  quality,
  qualityLabel,
  loading = false,
  footerExtras,
}: PieceCardProps) {
  const visuals = SOURCE_VISUALS[source];

  return (
    <div
      className="relative px-4 py-4"
      style={{
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.025) 0%, rgba(255, 255, 255, 0.01) 100%)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: "var(--radius-card)",
        boxShadow:
          "inset 0 1px 0 rgba(255, 255, 255, 0.03), 0 2px 6px rgba(0, 0, 0, 0.25)",
      }}
    >
      {/* Header: eyebrow + filename·size */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <Eyebrow>{visuals.eyebrow}</Eyebrow>
        <span
          className="text-[12px] tabular-nums truncate"
          style={{ color: "var(--color-text-muted)" }}
          title={`${filename} · ${fileSize}`}
        >
          {filename} · {fileSize}
        </span>
      </div>

      {/* Body: glyph + editable zone */}
      <div className="flex items-start gap-4">
        <FileGlyph glyph={visuals.glyph} badge={visuals.badge} />
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          {loading ? (
            <SkeletonMeta />
          ) : (
            <EditableMeta
              title={title}
              composer={composer}
              onTitleChange={onTitleChange}
              onComposerChange={onComposerChange}
              titlePlaceholder={titlePlaceholder}
              composerPlaceholder={composerPlaceholder}
            />
          )}
        </div>
      </div>

      {/* Separator */}
      <div
        className="my-2.5"
        style={{
          height: 1,
          backgroundColor: "var(--color-glass-separator)",
          opacity: 0.5,
        }}
      />

      {/* Footer grid: KEY · QUALITY */}
      <div className="grid grid-cols-2 gap-6">
        <FooterCell label="Tonalité">
          {loading ? (
            <SkeletonLine width={84} height={13} />
          ) : detectedKey ? (
            <span
              className="text-[13px] font-medium"
              style={{ color: "var(--color-text-primary)", opacity: 0.88 }}
            >
              {detectedKey}
            </span>
          ) : (
            <span
              className="text-[12px] italic"
              style={{ color: "var(--color-text-muted)" }}
            >
              Indétectable
            </span>
          )}
        </FooterCell>
        <FooterCell label="Qualité">
          {loading ? (
            <QualityDiamonds quality={0} ariaLabel="Qualité en cours d'analyse" />
          ) : (
            <span className="inline-flex items-center gap-2">
              <QualityDiamonds quality={quality ?? 0} />
              {qualityLabel && (
                <span
                  className="text-[11px]"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  {qualityLabel}
                </span>
              )}
            </span>
          )}
        </FooterCell>
      </div>

      {footerExtras && <div className="mt-3">{footerExtras}</div>}
    </div>
  );
}

// ─── Internal: editable meta block (title + composer) ───

interface EditableMetaProps {
  title: string;
  composer: string;
  onTitleChange?: (next: string) => void;
  onComposerChange?: (next: string) => void;
  titlePlaceholder: string;
  composerPlaceholder: string;
}

function EditableMeta({
  title,
  composer,
  onTitleChange,
  onComposerChange,
  titlePlaceholder,
  composerPlaceholder,
}: EditableMetaProps) {
  // `onChange` is required by InlineEditableField. When the parent did not
  // supply a handler, we fall back to a no-op so the card stays renderable
  // as a read-only summary (used by tests / preview surfaces).
  const noop = () => {};

  return (
    <>
      <InlineEditableField
        value={title}
        placeholder={titlePlaceholder}
        variant="title"
        onChange={onTitleChange ?? noop}
        ariaLabel="Titre du morceau"
      />
      <InlineEditableField
        value={composer}
        placeholder={composerPlaceholder}
        variant="composer"
        onChange={onComposerChange ?? noop}
        ariaLabel="Compositeur du morceau"
      />
    </>
  );
}

// ─── Internal: skeleton placeholders ───

function SkeletonMeta() {
  return (
    <>
      <div
        className="upload-skeleton rounded"
        style={{ height: 22, width: "62%" }}
        aria-hidden
      />
      <div
        className="upload-skeleton rounded"
        style={{ height: 13, width: "42%" }}
        aria-hidden
      />
    </>
  );
}

function SkeletonLine({ width, height }: { width: number; height: number }) {
  return (
    <div
      className="upload-skeleton rounded"
      style={{ width, height }}
      aria-hidden
    />
  );
}

// ─── Internal: footer cell ───

interface FooterCellProps {
  label: string;
  children: ReactNode;
}

function FooterCell({ label, children }: FooterCellProps) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <Eyebrow>{label}</Eyebrow>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

// ─── Internal: file glyph (mini piano-roll or staff lines) ───

interface FileGlyphProps {
  glyph: "roll" | "staff";
  badge: string;
}

const MIDI_BANDS: ReadonlyArray<{ leftPct: number; widthPct: number; opacity: number }> = [
  { leftPct: 0, widthPct: 70, opacity: 0.55 },
  { leftPct: 18, widthPct: 56, opacity: 0.4 },
  { leftPct: 8, widthPct: 78, opacity: 0.35 },
  { leftPct: 32, widthPct: 48, opacity: 0.25 },
  { leftPct: 4, widthPct: 86, opacity: 0.4 },
  { leftPct: 24, widthPct: 60, opacity: 0.3 },
];

function FileGlyph({ glyph, badge }: FileGlyphProps) {
  return (
    <div
      className="relative shrink-0 overflow-hidden"
      style={{
        width: 56,
        height: 72,
        borderRadius: 8,
        background:
          "linear-gradient(180deg, var(--color-violet-soft) 0%, transparent 100%)",
        border: "1px solid var(--color-border-subtle)",
        boxShadow:
          "inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 2px 6px rgba(0, 0, 0, 0.25)",
      }}
      aria-hidden
    >
      {glyph === "roll" ? (
        <div className="absolute inset-0 flex flex-col justify-center gap-[3px] px-2">
          {MIDI_BANDS.map((band, i) => (
            <span
              key={i}
              className="block rounded-[1px]"
              style={{
                marginLeft: `${band.leftPct}%`,
                width: `${band.widthPct}%`,
                height: 3,
                backgroundColor: "var(--color-accent-bright)",
                opacity: band.opacity,
              }}
            />
          ))}
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col justify-center gap-2 px-2">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className="block"
              style={{
                height: 1,
                backgroundColor: "var(--color-text-muted)",
                opacity: 0.3,
              }}
            />
          ))}
        </div>
      )}
      <span
        className="absolute bottom-1 right-1 text-[7px] font-bold uppercase"
        style={{
          color: "var(--color-text-muted)",
          letterSpacing: "0.08em",
        }}
      >
        {badge}
      </span>
    </div>
  );
}
